import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Wraps `React.lazy` to support named exports without repeating the
 * `.then((m) => ({ default: m.Name }))` boilerplate at every route-table
 * site. The generic constraints reject names that do not exist on the imported
 * module and reject names whose value is not a React component.
 *
 * @example
 *   const Home = lazyNamed(() => import('./pages/Home'), 'Home');
 */
export function lazyNamed<
  TModule,
  K extends keyof TModule,
>(
  loader: () => Promise<TModule>,
  name: TModule[K] extends ComponentType<unknown> ? K : never,
): LazyExoticComponent<ComponentType<unknown>> {
  return lazy(() =>
    loader().then((module) => ({
      default: module[name] as ComponentType<unknown>,
    })),
  );
}
